import { CardTitle } from './ui/card';
import { Button } from './ui/Button';
import { useArrivalReceiptStore } from 'store/arrivalReceiptStore';
import { formatDateTime } from 'utils/formatDate';
import { cn } from 'utils/classnames';
import { useAppStore } from '../store';
import { SCREEN_TYPES } from 'utils/constants';

// TODO: Add problem resolution link
// TODO: Add Continue and Problem actions

const getDispositionColor = (disposition?: string): string => {
  if (!disposition) return 'bg-slate-100 text-slate-600';
  const d = disposition.toLowerCase();
  if (d.includes('stock') || d.includes('good')) return 'bg-green-100 text-green-700';
  if (d.includes('return') || d.includes('reject')) return 'bg-red-100 text-red-700';
  if (d.includes('hold') || d.includes('review')) return 'bg-yellow-100 text-yellow-700';
  return 'bg-blue-100 text-blue-700';
};

const Field = ({ label, value }: { label: string; value?: string | number | null }) => (
  <div className="flex flex-col gap-1 border-b border-slate-100 py-1.5 sm:flex-row">
    <span className="text-sm font-semibold text-slate-600 sm:min-w-[180px] sm:shrink-0">
      {label}
    </span>
    <span className="break-all text-sm text-slate-900">{value ?? 'N/A'}</span>
  </div>
);

export const ReceivingDisposition = () => {
  const { currentArrivalReceipt, setCurrentArrivalReceipt } = useArrivalReceiptStore();
  const { setCurrentScreen } = useAppStore();

  const onComplete = (): void => {
    setCurrentArrivalReceipt(null);
    setCurrentScreen(SCREEN_TYPES.APP_SELECT);
  };

  const handleConfirm = (): void => {
    onComplete();
  };

  const onCancel = (): void => {
    setCurrentScreen(SCREEN_TYPES.RECEIVING);
  };

  const onProblemClick = (): void => {
    setCurrentScreen(SCREEN_TYPES.RECEIVING);
  };

  return (
    <div className="space-y-4 sm:space-y-5">
      <CardTitle className="mb-1 flex items-center justify-center gap-3 text-center text-xl font-extrabold tracking-tight text-slate-900 sm:mb-2 sm:text-2xl">
        Disposition
      </CardTitle>
      {currentArrivalReceipt?.responseState === 'problem' ||
      currentArrivalReceipt?.responseState === 'exception' ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-red-200 bg-red-50 p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-lg">⚠️</span>
              <span className="text-sm font-bold uppercase tracking-wide text-red-700">
                Issue Detected
              </span>
            </div>
            <p className="text-sm leading-relaxed text-red-800">
              {currentArrivalReceipt.responseMsg ||
                'There is an issue with this receipt. Please contact your supervisor.'}
            </p>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                Next Step
              </p>
              <p className="mt-0.5 text-sm font-bold text-slate-700">Move to Problem Resolution</p>
            </div>
            <span className="text-slate-400">→</span>
          </div>
          <div className="buttons-container">
            <Button
              type="button"
              variant="secondary"
              className="h-12 flex-1 rounded-xl text-base font-semibold"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="h-12 flex-1 rounded-xl text-base font-semibold shadow-sm"
              //   disabled={loadingProblemClicked}
              onClick={onProblemClick}
            >
              Problem
            </Button>
          </div>
        </div>
      ) : (
        <>
          {currentArrivalReceipt?.arrival_disposition && (
            <>
              <div className="text-sm font-bold tracking-wide text-slate-600">DISPOSITION</div>
              <div className="text-center">
                <div
                  className={cn(
                    'inline-block rounded-full px-4 py-2 text-sm font-semibold uppercase tracking-[0.6px] shadow-sm',
                    getDispositionColor(currentArrivalReceipt.arrival_disposition)
                  )}
                >
                  {currentArrivalReceipt.arrival_disposition}
                </div>
              </div>
            </>
          )}

          <div className="space-y-1 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <Field label="Tracking #" value={currentArrivalReceipt?.tracking_id} />
            <Field label="Transaction #" value={currentArrivalReceipt?.transaction_id} />
            <Field label="RMA ID" value={currentArrivalReceipt?.rma_id} />
            <Field label="Priority ID" value={currentArrivalReceipt?.priority_id} />
            <Field label="Vendor ID" value={currentArrivalReceipt?.vendor_id} />
            <Field label="Vendor Name" value={currentArrivalReceipt?.vendor_name} />
            <Field label="Customer ID" value={currentArrivalReceipt?.customer_id} />
            <Field label="Invoice ID" value={currentArrivalReceipt?.invoice_id} />
            <Field label="Receipt Status" value={currentArrivalReceipt?.receipt_status} />
            <Field label="Expected Lines" value={currentArrivalReceipt?.expected_line_cnt} />
            <Field
              label="Vendor Master Missing"
              value={currentArrivalReceipt?.vendor_master_missing_cnt}
            />
            <Field
              label="Exception Process Count"
              value={currentArrivalReceipt?.exception_process_cnt}
            />
            <Field
              label="Arrival Started"
              value={formatDateTime(currentArrivalReceipt?.arrival_started_datetime)}
            />
            <Field
              label="Arrival Finished"
              value={formatDateTime(currentArrivalReceipt?.arrival_finished_datetime)}
            />
            <Field
              label="Target Grading Confirmed"
              value={formatDateTime(currentArrivalReceipt?.target_grading_confirmed_datetime)}
            />
          </div>
          <div className="buttons-container !justify-center">
            <Button
              type="button"
              onClick={handleConfirm}
              className="h-12 w-full rounded-xl bg-blue-600 px-10 text-base font-bold text-white shadow-[0_4px_12px_rgba(0,123,255,0.35)] hover:bg-blue-700 sm:w-auto sm:rounded-full"
            >
              Continue
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
